// Bildirimin tonu (başarı / hata / uyarı / bilgi) çağıran taraftan gelmez:
// panelde ve portalda 500'den fazla `toast({...})` çağrısı vardır ve çoğu
// yalnızca bir başlık yazar. Ton bu yüzden metinden çıkarılır — her çağrıya
// elle bir alan eklemek yerine kural tek yerde durur; yeni bir çağrı da
// hiçbir şey yazmadan doğru rengi alır.
//
// Sıra bilinçlidir: çağıranın açık isteği > `destructive` varyantı >
// olumsuz kalıp > uyarı kalıbı > olumlu kalıp > bilgi. Olumsuz önce bakılır,
// çünkü "kaydedilemedi" hem olumsuz ekini hem "kaydedil…" gövdesini taşır;
// olumlu kural önce çalışsaydı hata yeşil görünürdü.

export const TOAST_TONES = ['success', 'error', 'warning', 'info'];

// JavaScript'in `\b` sınırı ASCII'dir: "ı", "ş", "ğ" kelime karakteri
// sayılmaz, yani `/ndı\b/` "kopyalandı" ile **eşleşmez** ve `/\bönce/`
// hiçbir zaman tutmaz. Sınırlar bu yüzden elle yazılır.
const WORD = 'a-zçğıöşü0-9';
const HEAD = `(?:^|[^${WORD}])`;
const TAIL = `(?=$|[^${WORD}])`;

const word = (body) => new RegExp(`${HEAD}(?:${body})${TAIL}`);
const suffix = (body) => new RegExp(`(?:${body})${TAIL}`);

// "…edilemedi", "…bulunamadı", "hata", "başarısız" …
const NEGATIVE_PATTERNS = [
  // Türkçe olumsuzluk ekleri: "…edilemedi", "…kabul edilmedi",
  // "…kullanılamıyor", "…açılamaz".
  suffix('medi|madı|mıyor|miyor|muyor|müyor|maz|mez'),
  word('hata|hatalı|hatası|başarısız|geçersiz|yanlış|sorun|zaman aşımı'),
  // "iptal edildi" bilinçli olarak listede **yoktur**: gerçekleşmiş bir
  // işlemdir ("Bonus iptal edildi"), hata değil. Başarısızlığı zaten
  // "iptal edilemedi" olumsuzluk eki yakalar.
  new RegExp(`${HEAD}(?:reddedil|engellen|yetkiniz|izin yok|bulunamadı|ulaşılamadı)`),
];

// Oyuncuya/yöneticiye "şunu yapman gerekiyor" diyen kalıplar: karar değil
// yönlendirmedir, kırmızı yazmak gereksiz bir arıza izlenimi verir.
const WARNING_PATTERNS = [
  word('zorunlu|gerekli|gerekiyor|eksik|yetersiz|boş|kapalı|çelişkili|uyarı|dikkat|önce|en az|en fazla|çok kısa|çok uzun|bekleniyor|bekleyin|emin misiniz'),
  new RegExp(`${HEAD}(?:seçin|seçiniz|girin|giriniz|yazın|doldurun|onaylayın|tanımlayın|kontrol edin)`),
  suffix('olmalı|olmalıdır'),
];

// Edilgen geçmiş zaman: "kaydedildi", "silindi", "kopyalandı", "yüklendi".
const POSITIVE_PATTERNS = [
  suffix('ldı|ldi|ldu|ldü|ndı|ndi|ndu|ndü|tıldı|tildi|rildi|rıldı'),
  word('başarılı|tamam|tamamlandı|hazır|güncel'),
];

const lower = (value) => (
  typeof value === 'string' ? value.toLocaleLowerCase('tr') : ''
);

const matches = (patterns, text) => patterns.some((pattern) => pattern.test(text));

/**
 * Bildirimin tonunu çözer.
 * @param {{tone?: string, variant?: string, title?: any, description?: any}} toast
 * @returns {'success'|'error'|'warning'|'info'}
 */
export const resolveToastTone = ({ tone, variant, title, description } = {}) => {
  if (TOAST_TONES.includes(tone)) return tone;
  // Eski çağrılar hatayı `variant: 'destructive'` ile bildiriyor; sözleşme
  // korunur, yoksa 22 çağrı sessizce bilgi rengine düşerdi.
  if (variant === 'destructive') return 'error';

  const head = lower(title);
  const body = `${head} ${lower(description)}`.trim();

  if (matches(NEGATIVE_PATTERNS, body)) return 'error';
  if (matches(WARNING_PATTERNS, body)) return 'warning';
  // Olumlu kalıp yalnızca başlıkta aranır: açıklama çoğu zaman bağlam
  // cümlesidir ("… listesi güncellendi") ve başlığı olmayan bir sonucu
  // başarı sanmak yanlış olurdu.
  if (matches(POSITIVE_PATTERNS, head)) return 'success';
  return 'info';
};

export default resolveToastTone;
